import React, { useState, FC } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "src/hooks/redux";
import { selectorCurrentDealIsLoading } from "src/redux/currentDeal/selector";
import { createDealFromCloneAsync } from "src/redux/currentDeal/actions";
import {
  addToFavoritesAsync,
  removeFromFavoritesAsync,
} from "src/redux/favorites/actions";
import { Button } from "src/components/Button";
import { ButtonVariants } from "src/components/Button/types";
import { ReactComponent as MessageWhiteIcon } from "src/assets/icons/message-white.svg";
import { ReactComponent as FavoriteIcon } from "src/assets/icons/bookmark.svg";
import { ReactComponent as FavoriteOutlineIcon } from "src/assets/icons/bookmark-outline.svg";
import { cloneItemTrns } from "../trns";

const DEFAULT_ACTION_BUTTON_CLASSNAME = "basis-1/2 flex items-center gap-2.5";

interface Props {
  id: number;
  isFavorite: boolean;
  isHost: boolean;
  isActiveDeal: boolean;
  setIsFavorite: (isFavorite: boolean) => void;
}

export const ActionButtons: FC<Props> = ({
  id,
  isFavorite,
  isHost,
  isActiveDeal,
  setIsFavorite,
}) => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const isLoadingDeal = useAppSelector(selectorCurrentDealIsLoading);

  const [isLoadingFavorite, setIsLoadingFavorite] = useState(false);

  const onSuccessAddToFavorites = () => {
    setIsFavorite(true);
    setIsLoadingFavorite(false);
  };

  const onSuccessRemoveFromFavorites = () => {
    setIsFavorite(false);
    setIsLoadingFavorite(false);
  };

  const createDeal = () => {
    dispatch(createDealFromCloneAsync(id));
  };

  const addToFavorites = () => {
    setIsLoadingFavorite(true);
    dispatch(addToFavoritesAsync({ id, onSuccess: onSuccessAddToFavorites }));
  };

  const removeFromFavorites = () => {
    setIsLoadingFavorite(true);
    dispatch(
      removeFromFavoritesAsync({ id, onSuccess: onSuccessRemoveFromFavorites })
    );
  };

  const handleAddToFavoriteButton = () => {
    if (isFavorite) {
      removeFromFavorites();
    } else {
      addToFavorites();
    }
  };

  const isDisabledDealBtn = isLoadingDeal || isHost || isActiveDeal;

  const Icon = isFavorite ? FavoriteIcon : FavoriteOutlineIcon;

  const labelFavoriteBtn = isFavorite
    ? cloneItemTrns.removeFromFavoritesButton
    : cloneItemTrns.addToFavoritesButton;

  return (
    <div className="flex flex-col md:flex-row gap-3 mt-auto">
      <Button
        className={DEFAULT_ACTION_BUTTON_CLASSNAME}
        variant={ButtonVariants.PRIMARY}
        onClick={createDeal}
        isLoading={isLoadingDeal}
        isDisabled={isDisabledDealBtn}
      >
        <MessageWhiteIcon />
        <span>{t(cloneItemTrns.startDealButton)}</span>
      </Button>

      <Button
        className={DEFAULT_ACTION_BUTTON_CLASSNAME}
        variant={ButtonVariants.GRAY}
        onClick={handleAddToFavoriteButton}
        isLoading={isLoadingFavorite}
        isDisabled={isLoadingFavorite}
      >
        <Icon />
        <span>{t(labelFavoriteBtn)}</span>
      </Button>
    </div>
  );
};
